import React from 'react';
import { motion } from 'framer-motion';
import {
  IconWorld,
  IconServer,
  IconDatabase,
  IconCloud,
  IconTools,
  IconLeaf,
  IconGitBranch,
  IconBrandReact,
  IconBrandNextjs,
  IconBrandTypescript,
  IconBrandJavascript,
  IconBrandHtml5,
  IconBrandCss3,
  IconBrandTailwind,
  IconBrandNodejs,
  IconBrandPython,
  IconBrandCpp,
  IconBrandDocker,
  IconBrandAws,
  IconBrandVscode,
  IconBrandMongodb,
  IconBrandMysql,
} from '@tabler/icons-react';

const categories = [
  {
    name: 'Web',
    icon: IconWorld,
    gradient: 'from-blue-500 to-cyan-500',
    techs: [
      { label: 'React', icon: IconBrandReact },
      { label: 'Next.js', icon: IconBrandNextjs },
      { label: 'TypeScript', icon: IconBrandTypescript },
      { label: 'JavaScript', icon: IconBrandJavascript },
      { label: 'HTML5', icon: IconBrandHtml5 },
      { label: 'CSS3', icon: IconBrandCss3 },
      { label: 'Tailwind', icon: IconBrandTailwind },
    ],
  },
  {
    name: 'Backend',
    icon: IconServer,
    gradient: 'from-cyan-500 to-indigo-500',
    techs: [
      { label: 'Node.js', icon: IconBrandNodejs },
      { label: 'Express', icon: null },
    { label: 'GraphQL', icon: null },
      { label: 'Flask', icon: null },
      { label: 'Python', icon: IconBrandPython },
  { label: 'Java', icon: null },
      { label: 'C++', icon: IconBrandCpp },
    ],
  },
  {
    name: 'Database',
    icon: IconDatabase,
    gradient: 'from-emerald-500 to-lime-500',
    techs: [
  { label: 'MongoDB', icon: IconBrandMongodb },
  { label: 'MySQL', icon: IconBrandMysql },
  { label: 'PostgreSQL', icon: null },
    ],
  },
  {
    name: 'DevOps & Cloud',
    icon: IconCloud,
    gradient: 'from-purple-500 to-pink-500',
    techs: [
      { label: 'AWS', icon: IconBrandAws },
      { label: 'Docker', icon: IconBrandDocker },
      { label: 'GitHub Actions', icon: IconGitBranch },
      { label: 'Jenkins', icon: null },
    ],
  },
  {
    name: 'Tools',
    icon: IconTools,
    gradient: 'from-orange-500 to-amber-500',
    techs: [
      { label: 'VS Code', icon: IconBrandVscode },
      { label: 'Postman', icon: null },
      { label: 'Git', icon: IconGitBranch },
    ],
  },
];

const TechChip = ({ label, Icon }) => (
  <div className="flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-white/90 hover:bg-white/15 transition-colors">
    {Icon ? <Icon size={18} /> : <span className="w-[18px]" />}
    <span className="text-sm">{label}</span>
  </div>
);

const CategoryTile = ({ name, icon: Icon, gradient, techs }) => {
  const [active, setActive] = React.useState(false);
  const [hovered, setHovered] = React.useState(false);
  const [suppressHover, setSuppressHover] = React.useState(false);
  const show = active || (hovered && !suppressHover);

  const toggle = () => {
    setActive((v) => {
      const next = !v;
      if (!next) {
        // If turning off via click, suppress hover until mouse leaves
        setSuppressHover(true);
      }
      return next;
    });
  };
  const handleKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  };
  const onEnter = () => {
    setHovered(true);
    if (!active) setSuppressHover(false);
  };
  const onLeave = () => {
    setHovered(false);
    setSuppressHover(false);
  };
  return (
    <div
      className="group relative overflow-hidden rounded-xl border border-white/10 bg-white/[0.02] backdrop-blur-sm hover:border-white/20 transition-transform duration-300 hover:scale-[1.02] hover:shadow-[0_0_40px_rgba(255,255,255,0.08)]"
      role="button"
      tabIndex={0}
      onClick={toggle}
      onKeyDown={handleKey}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      {/* Base content */}
      <div className="relative aspect-[4/3] flex flex-col items-center justify-center p-6">
        <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${gradient} opacity-10`} />
        <Icon className={`text-white/80 transition-opacity duration-300 ${show ? 'opacity-0' : ''}`} size={56} />
        <h3 className={`mt-3 text-lg font-semibold text-white/90 transition-opacity duration-300 ${show ? 'opacity-0' : ''}`}>{name}</h3>
        <p className={`text-xs text-white/50 transition-opacity duration-300 ${show ? 'opacity-0' : ''}`}>Hover to reveal</p>
      </div>

      {/* Overlay: chips grid; shows on hover (md+) or when active (mobile click) */}
      <div
        className={`absolute inset-0 flex items-center justify-center bg-black/80 backdrop-blur-sm transition-opacity duration-300 ${show ? 'opacity-100' : 'opacity-0'}`}
        // Prevent clicks inside overlay from toggling immediately if desired
        // onClick={(e) => e.stopPropagation()}
      >
        <div className="max-w-xs mx-auto grid grid-cols-2 gap-2 p-4">
          {techs.map((t, idx) => (
            <div
              key={idx}
              className={`transform transition-all duration-300 ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}
              style={{ transitionDelay: `${idx * 50}ms` }}
            >
              <TechChip label={t.label} Icon={t.icon} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const Skills = () => {
  return (
    <section className="container mx-auto px-4 pt-8 pb-16">
      <div className="max-w-6xl mx-auto text-center pt-8 pb-12">
        <h2 className="text-4xl font-bold bg-gradient-to-r from-white via-white/90 to-white text-transparent bg-clip-text">Skills</h2>
    <p className="text-gray-400 mt-4">A comprehensive overview of my technical expertise</p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
      >
        {categories.map((cat, i) => (
          <CategoryTile key={i} {...cat} />
        ))}
      </motion.div>
    </section>
  );
};

export default Skills;
